import { useState, useEffect } from 'react';
import { MessageCircle, Sparkles, X, Send, Loader2 } from 'lucide-react';
import api from '../../services/api';

// 🔽 Add this near the top of the file (after imports)
const isCulturalTeamEvent = (name = "") => {
  const keywords = [
    "folk dance",
    "contemporary dance",
    "street dance",
    "short and sweet play (dialogue)",
    "short and sweet play (musical)",
    "radio drama",
    "cheerdance",
    "live band",
    "table tennis",
    "badminton",
    "radio drama",
    "short and sweet play dialogue",
    "short and sweet play musical",
    "volleyball",
    "basketball",
    "mobile legends",
    "chess",
    "sepak takraw",
    "tug of war",
    "quiz bowl",
  ];

  return keywords.some(k => name.toLowerCase().includes(k));
};

// ✅ Define TeamCard FIRST
function TeamCard({ name, logo, score }) {
  return (
    <div className="flex items-center gap-3">
      <img
        src={logo || '/placeholder.png'}
        alt={name}
        className="w-10 h-10 rounded-full border"
      />
      <span className="font-medium">{name}</span>
      <span className="ml-2 px-2 py-1 bg-gray-100 rounded text-sm">{score}</span>
    </div>
  );
}

// 🤖 AI Score Insight Component - SIMPLE VERSION WITHOUT API
function AIScoreInsight({ overallScores, selectedEvent }) {
  const [insight, setInsight] = useState('');

  useEffect(() => {
    if (overallScores.length > 0) {
      generateSimpleInsight();
    }
  }, [overallScores, selectedEvent]);

  const generateSimpleInsight = () => {
    const top3 = overallScores.slice(0, 3);
    const leader = top3[0];
    const second = top3[1];
    const third = top3[2];

    if (!leader) return;

    // Calculate point differences
    const leadGap = second ? leader.total - second.total : 0;
    const secondThirdGap = second && third ? second.total - third.total : 0;

    let insights = [];

    // Generate contextual insights
    if (leadGap === 0) {
      insights.push(`🔥 It's a dead heat at the top! ${leader.college} and ${second.college} are tied at ${leader.total} points!`);
    } else if (leadGap <= 10) {
      insights.push(`⚡ Intense competition! ${leader.college} leads by just ${leadGap} points over ${second.college}. Anyone can win this!`);
    } else if (leadGap <= 25) {
      insights.push(`💪 ${leader.college} is pulling ahead with ${leader.total} points, but ${second.college} is staying in the fight at ${second.total} points!`);
    } else {
      insights.push(`🏆 ${leader.college} is dominating with ${leader.total} points! Can anyone catch up?`);
    }

    if (second && third && secondThirdGap <= 5) {
      insights.push(`The battle for second place is heating up between ${second.college} and ${third.college}!`);
    }

    setInsight(insights.join(' '));
  };

  if (overallScores.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border-2 border-yellow-300 shadow-md mb-6">
      <div className="flex items-start gap-3">
        <Sparkles className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
        <div className="flex-1">
          <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
            AI Commentary
          </h3>
          <p className="text-gray-700 text-sm italic">{insight}</p>
        </div>
        <button
          onClick={generateSimpleInsight}
          className="text-yellow-600 hover:text-yellow-700 text-xs underline"
        >
          Refresh
        </button>
      </div>
    </div>
  );
}

// 🤖 AI Chatbot Component - ENHANCED WITH EVENT WINNERS
function AIChatbot({ events, overallScores }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm your Intramurals AI Assistant. Ask me about events, scores, winners, or who's leading! 🏆"
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch event results for a specific event
  const fetchEventResults = async (eventId, isSport) => {
    try {
      if (isSport) {
        const res = await api.get(`/matches/event/${eventId}`);
        return res.data;
      } else {
        const res = await api.get(`/eventresults/${eventId}`);
        return res.data;
      }
    } catch (error) {
      console.error('Error fetching event results:', error);
      return [];
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    const userQuestion = input.toLowerCase();
    setInput('');
    setLoading(true);

    // Simulate thinking
    await new Promise(resolve => setTimeout(resolve, 800));

    let reply = '';

    try {
      // Check if asking about specific event winner
      const eventMatch = events.find(e => 
        userQuestion.includes(e.name.toLowerCase()) ||
        (e.name.toLowerCase().includes('basketball') && userQuestion.includes('basketball')) ||
        (e.name.toLowerCase().includes('volleyball') && userQuestion.includes('volleyball')) ||
        (e.name.toLowerCase().includes('mobile legends') && userQuestion.includes('mobile legend')) ||
        (e.name.toLowerCase().includes('folk dance') && userQuestion.includes('folk dance')) ||
        (e.name.toLowerCase().includes('chess') && userQuestion.includes('chess')) ||
        (e.name.toLowerCase().includes('badminton') && userQuestion.includes('badminton')) ||
        (e.name.toLowerCase().includes('table tennis') && userQuestion.includes('table tennis')) ||
        (e.name.toLowerCase().includes('lawn tennis') && userQuestion.includes('lawn tennis')) ||
        (e.name.toLowerCase().includes('vocal') && userQuestion.includes('vocal')) ||
        (e.name.toLowerCase().includes('dance') && userQuestion.includes('dance')) ||
        (e.name.toLowerCase().includes('sepak takraw') && userQuestion.includes('sepak')) ||
        (e.name.toLowerCase().includes('tug of war') && userQuestion.includes('tug')) ||
        (e.name.toLowerCase().includes('quiz bowl') && userQuestion.includes('quiz')) ||
        (e.name.toLowerCase().includes('live band') && userQuestion.includes('band')) ||
        (e.name.toLowerCase().includes('cheerdance') && userQuestion.includes('cheer'))
      );

      if ((userQuestion.includes('winner') || userQuestion.includes('won') || userQuestion.includes('champion')) && eventMatch) {
        // Fetch results for specific event
        const results = await fetchEventResults(eventMatch.id, eventMatch.is_sport);
        
        if (results.length === 0) {
          reply = `⏳ The ${eventMatch.name} competition is still ongoing. Winners will be announced soon!`;
        } else if (eventMatch.is_sport) {
          // For sports, find the winner
          const winner = results.find(m => m.winner_name);
          if (winner) {
            reply = `🏆 **${eventMatch.name}** Winner:\n\n🥇 ${winner.winner_name}!`;
          } else {
            reply = `⏳ The ${eventMatch.name} is still in progress. No winner announced yet.`;
          }
        } else {
          // For non-sports, show top 3
          const sorted = results.sort((a, b) => (a.placement || 999) - (b.placement || 999));
          const top3 = sorted.slice(0, 3);
          
          if (top3.length > 0 && top3[0].placement === 1) {
            reply = `🏆 **${eventMatch.name}** Results:\n\n`;
            top3.forEach((r, i) => {
              const medal = i === 0 ? '🥇 Champion' : i === 1 ? '🥈 1st Place' : '🥉 2nd Place';
              reply += `${medal}: ${r.name}\n`;
            });
          } else {
            reply = `⏳ The ${eventMatch.name} competition is still ongoing. Results will be posted soon!`;
          }
        }
      }
      // List all events
      else if (userQuestion.includes('list') && userQuestion.includes('event')) {
        reply = `📋 **All Intramurals Events (${events.length} total):**\n\n`;
        const categories = {
          sports: [],
          cultural: [],
          academic: []
        };
        
        events.forEach(e => {
          const name = `• ${e.name}${e.gender ? ` (${e.gender})` : ''}`;
          if (e.is_sport) {
            categories.sports.push(name);
          } else if (e.name.toLowerCase().includes('dance') || 
                     e.name.toLowerCase().includes('band') || 
                     e.name.toLowerCase().includes('vocal') ||
                     e.name.toLowerCase().includes('drama') ||
                     e.name.toLowerCase().includes('play')) {
            categories.cultural.push(name);
          } else {
            categories.academic.push(name);
          }
        });
        
        if (categories.sports.length > 0) {
          reply += `\n🏀 **Sports:**\n${categories.sports.join('\n')}\n`;
        }
        if (categories.cultural.length > 0) {
          reply += `\n🎭 **Cultural:**\n${categories.cultural.join('\n')}\n`;
        }
        if (categories.academic.length > 0) {
          reply += `\n🧠 **Academic:**\n${categories.academic.join('\n')}`;
        }
      }
      // Simple rule-based responses
      else if (userQuestion.includes('lead') || userQuestion.includes('first') || userQuestion.includes('winning')) {
        if (overallScores.length > 0) {
          const leader = overallScores[0];
          reply = `🏆 ${leader.college} is currently leading with ${leader.total} points! They're doing an amazing job! The competition is fierce though - `;
          if (overallScores[1]) {
            reply += `${overallScores[1].college} is in second place with ${overallScores[1].total} points.`;
          }
        } else {
          reply = "The competition is still underway! Overall rankings will be available once events conclude.";
        }
      } 
      else if (userQuestion.includes('top 3') || userQuestion.includes('top three') || userQuestion.includes('top colleges')) {
        if (overallScores.length >= 3) {
          reply = `Here are the top 3 colleges:\n\n🥇 ${overallScores[0].college} - ${overallScores[0].total} pts\n🥈 ${overallScores[1].college} - ${overallScores[1].total} pts\n🥉 ${overallScores[2].college} - ${overallScores[2].total} pts`;
        } else {
          reply = "The top 3 rankings will be available as more events conclude!";
        }
      }
      else if (userQuestion.includes('event') && (userQuestion.includes('ongoing') || userQuestion.includes('current') || userQuestion.includes('now') || userQuestion.includes('happening'))) {
        if (events.length > 0) {
          reply = `We have ${events.length} events in the intramurals! Here are some of them:\n\n`;
          events.slice(0, 5).forEach(e => {
            reply += `• ${e.name}${e.gender ? ` (${e.gender})` : ''}\n`;
          });
          if (events.length > 5) {
            reply += `\n...and ${events.length - 5} more events!`;
          }
        } else {
          reply = "Event information is being updated. Please check back soon!";
        }
      }
      else if (userQuestion.includes('event') && (userQuestion.includes('finish') || userQuestion.includes('done') || userQuestion.includes('complete'))) {
        reply = "Events are concluding throughout the intramurals! Check the leaderboard for the latest results and winners.";
      }
      else if (userQuestion.includes('how many') && userQuestion.includes('event')) {
        reply = `There are ${events.length} exciting events in this year's intramurals! From sports to cultural competitions, there's something for everyone!`;
      }
      else if (userQuestion.includes('score') || userQuestion.includes('point')) {
        if (overallScores.length > 0) {
          reply = `Current standings:\n\n`;
          overallScores.slice(0, 5).forEach((s, i) => {
            const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`;
            reply += `${medal} ${s.college} - ${s.total} pts\n`;
          });
        } else {
          reply = "Scores are being tallied! Check back soon for the latest rankings.";
        }
      }
      else if (userQuestion.includes('thank')) {
        reply = "You're welcome! Happy to help you stay updated with the intramurals! 🎉";
      }
      else if (userQuestion.includes('hello') || userQuestion.includes('hi ') || userQuestion === 'hi') {
        reply = "Hello! 👋 I'm here to answer your questions about the intramurals. Ask me about scores, events, or who's leading!";
      }
      else {
        // Default response with helpful suggestions
        reply = "I can help you with:\n• Who's leading overall\n• Current event standings\n• List of events\n• Top performing colleges\n\nWhat would you like to know?";
      }

      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (error) {
      console.error('Chatbot error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I encountered an error. Please try asking again!"
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 z-50 flex items-center gap-2"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 bg-red-500 text-xs w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
            AI
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 max-w-[calc(100vw-3rem)] h-[500px] bg-white rounded-lg shadow-2xl flex flex-col z-50 border-2 border-yellow-400">
          {/* Header */}
          <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              <h3 className="font-bold">Intramurals AI</h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 rounded p-1 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-yellow-500 text-white rounded-br-none'
                      : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none'
                  }`}
                >
                  <p className="text-sm whitespace-pre-line">{msg.content}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 p-3 rounded-lg rounded-bl-none">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about scores, events..."
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 text-sm"
                disabled={loading}
              />
              <button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="bg-yellow-500 text-white p-2 rounded-lg hover:bg-yellow-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                onClick={() => setInput("Who is leading overall?")}
                className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition"
              >
                Who's leading?
              </button>
              <button
                onClick={() => setInput("List all events")}
                className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition"
              >
                List events
              </button>
              <button
                onClick={() => setInput("Who won basketball?")}
                className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition"
              >
                Event winners?
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ✅ College Logos (online URLs)
const collegeLogos = {
  'College of Engineering': 'https://i.postimg.cc/xdwFtzL3/COE.png',
  'College of Computing and Information Systems': 'https://i.postimg.cc/Gm97RQQj/CCIS.png',
  'College of Liberal Arts and Sciences': 'https://i.postimg.cc/fW2hn9NY/CAS.jpg',
  'College of Business Technology': 'https://i.postimg.cc/BQzsD17J/COT.png',
  'College of Teacher Education': 'https://i.postimg.cc/c4L4fWsB/CTE.jpg',
};

export default function Leaderboard() {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('overall');
  const [event, setEvent] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [overallScores, setOverallScores] = useState([]);

  // ✅ Fetch all events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await api.get('/events');
        setEvents(res.data);
      } catch (err) {
        console.error('Failed to load events', err);
      }
    };
    fetchEvents();
  }, []);

  // ✅ Fetch leaderboard data (single event or overall)
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedEventId) return;
      setLoading(true);

      try {
        if (selectedEventId === 'overall') {
          // Fetch all results from all events for overall computation
          const allResults = [];
          for (const e of events) {
            if (!e.id) continue;
            const res = e.is_sport
              ? await api.get(`/matches/event/${e.id}`)
              : await api.get(`/eventresults/${e.id}`);
            allResults.push({ event: e, data: res.data });
          }

          // ✅ Compute total points by college
          const totals = {};
          allResults.forEach((ev) => {
            ev.data.forEach((r) => {
              const college = r.name || r.team1_name || r.team2_name || 'Unknown';
              let points = 0;

              // 🏆 Unified scoring logic
              if (ev.event.is_sport || isCulturalTeamEvent(ev.event.name)) {
                // SPORTS + CULTURAL TEAM EVENTS (same points)
                if (r.placement === 1 || r.winner_name) points = 25;
                else if (r.placement === 2) points = 20;
                else if (r.placement === 3) points = 15;
                else if (r.placement === 4) points = 10;
                else if (r.placement === 5) points = 5;
              } else {
                // INDIVIDUAL EVENTS
                if (r.placement === 1) points = 15;
                else if (r.placement === 2) points = 12;
                else if (r.placement === 3) points = 9;
                else if (r.placement === 4) points = 6;
                else if (r.placement === 5) points = 3;
              }

              if (!totals[college]) totals[college] = 0;
              totals[college] += points;
            });
          });

          const ranked = Object.entries(totals)
            .map(([college, total]) => ({ college, total }))
            .sort((a, b) => b.total - a.total);

          setOverallScores(ranked);
          setEvent({ name: 'Overall Score' });
          setResults([]);
        } else {
          // ✅ Fetch selected single event
          const eventRes = await api.get(`/events/${selectedEventId}`);
          const selected = eventRes.data;
          setEvent(selected);

          let resData;
          if (selected.is_sport) {
            resData = await api.get(`/matches/event/${selectedEventId}`);
          } else {
            resData = await api.get(`/eventresults/${selectedEventId}`);
          }
          setResults(resData.data);
        }
      } catch (err) {
        console.error('Failed to fetch leaderboard data', err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedEventId, events]);

  // ✅ Get icons for events
  const getEventIcon = (eventName = '') => {
    const lower = eventName.toLowerCase();
    if (lower.includes('basketball')) return '🏀';
    if (lower.includes('volleyball')) return '🏐';
    if (lower.includes('badminton')) return '🏸';
    if (lower.includes('tennis')) return '🎾';
    if (lower.includes('table')) return '🏓';
    if (lower.includes('arnis')) return '🥋';
    if (lower.includes('chess')) return '♟️';
    if (lower.includes('tug')) return '🤼';
    if (lower.includes('mobile legend')) return '🎮';
    if (lower.includes('dance')) return '💃';
    if (lower.includes('band') || lower.includes('music')) return '🎶';
    if (lower.includes('vocal') || lower.includes('sing')) return '🎤';
    if (lower.includes('quiz') || lower.includes('essay')) return '🧠';
    if (lower.includes('play') || lower.includes('drama')) return '🎭';
    if (lower.includes('folkdance')) return '🪗';
    return '🏅';
  };

  // ✅ Get label for overall rank
  const getRankLabel = (rank) => {
    switch (rank) {
      case 1:
        return '🏆';
      case 2:
        return '🥈';
      case 3:
        return '🥉';
      case 4:
        return '4th';
      case 5:
        return '5th';
      default:
        return '';
    }
  };

  // ✅ Render results for normal event
  const renderEventResults = () => {
    if (loading) return <p className="text-gray-500">Loading results...</p>;
    if (!results.length)
      return (
        <div className="text-center py-10">
          <img
            src="/snsulogo.png"
            alt="Logo"
            className="w-20 mx-auto mb-4 animate-pulse opacity-70"
          />
          <p className="text-lg font-semibold text-yellow-600">
            ⏳ Competition in Progress!
          </p>
          <p className="text-gray-500 text-sm">
            No winner has been announced yet for this event.
          </p>
        </div>
      );

    if (event?.is_sport) {
      return (
        <div className="space-y-4">
          {results.map((match) => (
            <div
              key={match.id}
              className="bg-white p-4 rounded-lg shadow flex flex-col md:flex-row items-center justify-between gap-4 border border-gray-100 hover:shadow-lg transition"
            >
              <TeamCard
                name={match.team1_name}
                logo={match.team1_logo}
                score={match.team1_score}
              />
              <span className="text-2xl font-bold text-gray-600">VS</span>
              <TeamCard
                name={match.team2_name}
                logo={match.team2_logo}
                score={match.team2_score}
              />
              {match.winner_name && (
                <div className="ml-4 text-green-600 font-bold">
                  🏆 {match.winner_name}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-2 mt-4">
        {results
          .sort((a, b) => (a.placement || 999) - (b.placement || 999))
          .map((r) => {
            let rankEmoji = '';
            if (r.placement === 1) rankEmoji = '🥇 Champion';
            else if (r.placement === 2) rankEmoji = '🥈 1st Place';
            else if (r.placement === 3) rankEmoji = '🥉 2nd Place';
            else rankEmoji = '🎖️ Participant';

            return (
              <div
                key={r.college_id}
                className="flex items-center p-3 bg-white rounded-lg shadow border border-gray-100 hover:bg-yellow-50 transition"
              >
                <span
                  className={`font-bold mr-3 ${
                    r.placement === 1
                      ? 'text-yellow-500'
                      : r.placement === 2
                      ? 'text-gray-500'
                      : r.placement === 3
                      ? 'text-orange-500'
                      : 'text-gray-600'
                  }`}
                >
                  {rankEmoji}
                </span>
                <img
                  src={r.logo_url || '/placeholder.png'}
                  alt={r.name}
                  className="w-10 h-10 rounded-full border mr-3"
                />
                <span className="font-medium">{r.name}</span>
              </div>
            );
          })}
      </div>
    );
  };

  // ✅ Render overall leaderboard
  const renderOverallLeaderboard = () => (
    <div className="mt-4 space-y-2">
      {overallScores.map((row, index) => (
        <div
          key={row.college}
          className={`flex items-center justify-between p-3 rounded-lg border shadow ${
            index === 0
              ? 'bg-yellow-100 border-yellow-400 font-bold'
              : index === 1
              ? 'bg-gray-100 border-gray-300'
              : index === 2
              ? 'bg-orange-100 border-orange-300'
              : 'bg-white border-gray-200'
          }`}
        >
          <div className="flex items-center gap-3">
            <span className="text-lg font-semibold">{getRankLabel(index + 1)}</span>
            <img
              src={collegeLogos[row.college] || '/placeholder.png'}
              alt={row.college}
              className="w-10 h-10 rounded-full border"
            />
            <span className="text-gray-700">{row.college}</span>
          </div>
          <span className="text-blue-700 font-bold">{row.total} pts</span>
        </div>
      ))}
    </div>
  );

  return (
    <section className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-3 flex items-center gap-2 justify-center">
        🏆 Rankings
        <span className="ml-2 bg-yellow-500 text-white text-sm px-2 py-1 rounded-full animate-pulse">
          LIVE
        </span>
      </h2>
      <p className="text-sm text-gray-500 mb-4 text-center">
        Stay updated with the latest scores and rankings. Check back regularly for event results!
      </p>

      {/* 🤖 AI Score Insight - Shows only on Overall view */}
      {selectedEventId === 'overall' && overallScores.length > 0 && (
        <AIScoreInsight 
          overallScores={overallScores}
          selectedEvent={selectedEventId !== 'overall' ? event?.name : null}
        />
      )}

      {/* Desktop buttons */}
      <div className="hidden md:flex flex-wrap gap-2 mb-6 justify-center">
        <button
          onClick={() => setSelectedEventId('overall')}
          className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium border transition ${
            selectedEventId === 'overall'
              ? 'bg-yellow-500 text-white border-yellow-600 scale-105'
              : 'bg-gray-100 text-gray-700 hover:bg-yellow-100 hover:scale-105'
          }`}
        >
          🧮 Overall Score
        </button>

        {events.map((e) => (
          <button
            key={e.id}
            onClick={() => setSelectedEventId(e.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium border transition ${
              selectedEventId === e.id
                ? 'bg-yellow-500 text-white border-yellow-600 scale-105'
                : 'bg-gray-100 text-gray-700 hover:bg-yellow-100 hover:scale-105'
            }`}
          >
            <span>{getEventIcon(e.name)}</span>
            {e.name} {e.gender ? `(${e.gender})` : ''}
          </button>
        ))}
      </div>

      {/* Mobile dropdown */}
      <div className="md:hidden mb-6 flex justify-center">
        <div className="w-3/4 relative">
          <select
            className="w-full p-3 border-2 border-yellow-400 bg-yellow-50 text-gray-800 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 appearance-none text-center font-medium"
            value={selectedEventId || ''}
            onChange={(e) => setSelectedEventId(e.target.value || null)}
          >
            <option value="">📋 Select an Event</option>
            <option value="overall">🧮 Overall Score</option>
            {events.map((e) => (
              <option key={e.id} value={e.id}>
                {getEventIcon(e.name)} {e.name}{' '}
                {e.gender ? `(${e.gender})` : ''}
              </option>
            ))}
          </select>

          <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-yellow-600">
            ▼
          </div>
        </div>
      </div>

      {/* Render results */}
      {selectedEventId === 'overall'
        ? renderOverallLeaderboard()
        : selectedEventId
        ? (
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-4">{event?.name || 'Loading...'}</h3>
            {renderEventResults()}
          </div>
        )
        : (
          <div className="text-center text-gray-500 italic">
            Select an event to view standings.
          </div>
        )}

      {/* 🤖 AI Chatbot - Floating button */}
      <AIChatbot events={events} overallScores={overallScores} />
    </section>
  );
}